import React, { useState } from 'react';
import { Box, Container, Typography, Button, Switch, IconButton } from '@mui/material';
import { Link } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import { useCookieConsent } from '../hooks/useCookieConsent';

export default function CookieBanner() {
  const { hasConsent, saveConsent } = useCookieConsent();
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always required
    analytics: false,
    marketing: false,
  });

  // Don't show banner if user has already consented
  if (hasConsent) return null;

  const saveCookiePreferences = (prefs) => {
    saveConsent(prefs);
  };

  const acceptAll = () => {
    saveCookiePreferences({
      essential: true,
      analytics: true,
      marketing: true,
    });
  };

  const rejectNonEssential = () => {
    saveCookiePreferences({
      essential: true,
      analytics: false,
      marketing: false,
    });
  };

  const saveCustomPreferences = () => {
    saveCookiePreferences(preferences);
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        bgcolor: 'rgba(10, 14, 39, 0.5)',
        backdropFilter: 'blur(8px)',
        borderTop: '1px solid rgba(147, 51, 234, 0.2)',
        animation: 'fadeIn 0.3s ease-in',
        '@keyframes fadeIn': {
          from: { opacity: 0, transform: 'translateY(20px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      <Container maxWidth="lg" sx={{ py: 2 }}>
        {!showSettings ? (
          // Simple banner
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="white" sx={{ mb: 1 }}>
                We use cookies to improve your experience, provide essential functionality, and analyze our traffic.
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                By clicking "Accept All", you consent to our use of cookies. Learn more in our{' '}
                <Link to="/privacy" style={{ color: '#667eea', textDecoration: 'underline' }}>
                  Privacy Policy
                </Link>
                {' and '}
                <Link to="/terms" style={{ color: '#667eea', textDecoration: 'underline' }}>
                  Terms & Conditions
                </Link>
                .
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowSettings(true)}
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.4)',
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                Settings
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={rejectNonEssential}
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.4)',
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                Essential Only
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={acceptAll}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  },
                }}
              >
                Accept All
              </Button>
            </Box>
          </Box>
        ) : (
          // Detailed settings
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" color="white" sx={{ fontWeight: 600 }}>
                Cookie Preferences
              </Typography>
              <IconButton
                onClick={() => setShowSettings(false)}
                size="small"
                sx={{ color: 'rgba(255, 255, 255, 0.6)', '&:hover': { color: 'white' } }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                gap: 2,
                mb: 2,
              }}
            >
              {/* Essential Cookies */}
              <Box sx={{ p: 2, bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" color="white" sx={{ fontWeight: 600 }}>
                    Essential
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                      color: 'rgba(255, 255, 255, 0.6)',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                    }}
                  >
                    Always On
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1.5 }}>
                  Required for basic site functionality, security, and checkout process.
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                  Session management, form data, security tokens
                </Typography>
              </Box>

              {/* Analytics Cookies */}
              <Box sx={{ p: 2, bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" color="white" sx={{ fontWeight: 600 }}>
                    Analytics
                  </Typography>
                  <Switch
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences((prev) => ({ ...prev, analytics: e.target.checked }))}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#667eea',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#667eea',
                      },
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1.5 }}>
                  Help us understand how visitors use our site to improve performance.
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                  Page views, user behavior, site performance
                </Typography>
              </Box>

              {/* Marketing Cookies */}
              <Box sx={{ p: 2, bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" color="white" sx={{ fontWeight: 600 }}>
                    Marketing
                  </Typography>
                  <Switch
                    checked={preferences.marketing}
                    onChange={(e) => setPreferences((prev) => ({ ...prev, marketing: e.target.checked }))}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#667eea',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#667eea',
                      },
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1.5 }}>
                  Enable personalized ads and measure advertising effectiveness.
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                  Ad targeting, campaign measurement, remarketing
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                pt: 2,
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                Learn more:{' '}
                <Link to="/privacy" style={{ color: '#667eea', textDecoration: 'underline' }}>
                  Privacy Policy
                </Link>
                {' • '}
                <Link to="/terms" style={{ color: '#667eea', textDecoration: 'underline' }}>
                  Terms & Conditions
                </Link>
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={rejectNonEssential}
                  sx={{
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.4)',
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  Essential Only
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={saveCustomPreferences}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                    },
                  }}
                >
                  Save Preferences
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
}
